
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Minimize, Maximize } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Email } from '@/services/emailService';
import EmailRecipientFields from './compose/EmailRecipientFields';
import EmailBottomToolbar from './compose/EmailBottomToolbar';
import RichEmailEditor, { RichEmailEditorHandle } from './editor/RichEmailEditor';
import { useEmailCompose } from '@/hooks/email/useEmailCompose';
import { useDefaultEmailSignature } from '@/hooks/email/useEmailSignatures';
import { EmailSignature } from '@/services/api/emailSignatures';

interface ComposeEmailProps {
  initialEmail?: Partial<Email>;
  isReply?: boolean;
  isReplyAll?: boolean;
  isForward?: boolean;
  onClose: () => void;
  onMinimize?: () => void;
  draftId?: string;
  onSent?: () => void;
  leadId?: string;
  contactId?: string;
}

const ComposeEmail: React.FC<ComposeEmailProps> = ({
  initialEmail,
  isReply = false,
  isReplyAll = false,
  isForward = false,
  onClose,
  onMinimize,
  draftId,
  onSent,
  leadId,
  contactId,
}) => {
  const {
    toAddress,
    ccAddress,
    bccAddress,
    subject,
    bodyHtml,
    showCcBcc,
    isMaximized,
    isSavingDraft,
    isSending,
    setToAddress,
    setCcAddress,
    setBccAddress,
    setSubject,
    setBodyHtml,
    setShowCcBcc,
    setIsMaximized,
    handleChange,
    handleSend,
    handleSaveDraft,
    handleCloseWithAutoSave,
  } = useEmailCompose({
    initialEmail,
    isReply,
    isReplyAll,
    isForward,
    onClose,
    draftId,
    onSent,
    leadId,
    contactId,
  });

  // Signature handling — the UI owns the final composed body; the server
  // never appends signatures. The applied signature is wrapped in a marker
  // div so picking a different one replaces it instead of stacking.
  const { data: defaultSignature } = useDefaultEmailSignature();
  const appliedSigRef = useRef<{ id: string; html: string } | null>(null);
  const [appliedSignatureId, setAppliedSignatureId] = useState<string | null>(null);
  const defaultSigAppliedRef = useRef(false);

  const SIG_RE = /<div data-signature="true">[\s\S]*?<\/div>/;
  /** Blank paragraphs so the caret always has room above the signature. */
  const TYPING_ROOM = '<p></p><p></p>';

  const stripSignature = useCallback((html: string) => {
    const applied = appliedSigRef.current;
    if (applied?.html && html.includes(applied.html)) {
      return html.replace(applied.html, '');
    }
    return html.replace(SIG_RE, '');
  }, []);

  const composeWithSignature = useCallback((base: string, signature: EmailSignature | null) => {
    if (!signature?.body_html) return base;
    const wrapped = `<div data-signature="true">${signature.body_html}</div>`;
    appliedSigRef.current = { id: signature.id, html: wrapped };
    // Always keep writing room above the signature block
    const room = base.trim() ? '' : TYPING_ROOM;
    const separator = base.trim() && !/<br\s*\/?>\s*$/.test(base) ? '<br>' : '';
    return `${base}${room}${separator}${wrapped}`;
  }, []);

  const applySignature = useCallback((signature: EmailSignature | null, markDirty = true) => {
    setBodyHtml((prev) => {
      const base = stripSignature(prev);
      if (!signature?.body_html) {
        appliedSigRef.current = null;
        setAppliedSignatureId(null);
        return base;
      }
      setAppliedSignatureId(signature.id);
      return composeWithSignature(base, signature);
    });
    if (markDirty) handleChange();
  }, [setBodyHtml, handleChange, stripSignature, composeWithSignature]);

  // Pre-select the user's default signature on a brand-new compose only
  // (not replies/forwards/drafts). Doesn't mark dirty so auto-save stays idle.
  const isFreshCompose = !isReply && !isReplyAll && !isForward && !draftId;
  useEffect(() => {
    if (!isFreshCompose || defaultSigAppliedRef.current) return;
    if (!defaultSignature?.body_html) return;
    defaultSigAppliedRef.current = true;
    applySignature(defaultSignature, false);
  }, [defaultSignature, isFreshCompose, applySignature]);

  /**
   * Templates replace the message body — re-attach the currently applied
   * signature (and keep typing room) so it never gets wiped out.
   */
  const currentSignature = useRef<EmailSignature | null>(null);
  const setBodyFromTemplate = useCallback((html: string) => {
    const applied = appliedSigRef.current;
    const sig = currentSignature.current;
    const base = stripSignature(html);
    if (!applied || !sig) {
      setBodyHtml(base);
      return;
    }
    setBodyHtml(`${base}<p></p>${applied.html}`);
  }, [setBodyHtml, stripSignature]);

  useEffect(() => {
    if (defaultSignature && appliedSignatureId === defaultSignature.id) {
      currentSignature.current = defaultSignature;
    }
  }, [defaultSignature, appliedSignatureId]);

  const editorRef = useRef<RichEmailEditorHandle>(null);
  const cursorPlacedRef = useRef<boolean>(false);

  // Place the caret at the top of the body: above the quoted message for
  // replies, above the signature for a fresh compose.
  useEffect(() => {
    if (cursorPlacedRef.current) return;
    if (!bodyHtml) return;
    cursorPlacedRef.current = true;
    editorRef.current?.focus('start');

  }, [isReply, bodyHtml]);

  return (
    <div className={`
      fixed rounded-t-lg shadow-lg bg-background border border-border
      ${isMaximized 
        ? 'inset-0 z-50 rounded-none' 
        : 'right-4 bottom-0 w-full sm:w-[45rem] z-40'
      }
      flex flex-col
    `}>
      {/* Compact Header */}
      <div className={`bg-card px-3 py-2 flex items-center justify-between border-b ${isMaximized ? 'rounded-none' : 'rounded-t-lg'}`}>
        <h3 className="font-medium text-sm">
          {draftId ? 'Edit Draft' : isReplyAll ? 'Reply All' : isReply ? 'Reply' : isForward ? 'Forward' : 'New Message'}
        </h3>
        <div className="flex items-center space-x-1">
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setIsMaximized(!isMaximized)}>
            {isMaximized ? (
              <Minimize className="h-3 w-3" />
            ) : (
              <Maximize className="h-3 w-3" />
            )}
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCloseWithAutoSave}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto bg-background flex flex-col min-h-0">
        {/* Recipient Fields */}
        <div className="p-3 pb-2">
          <EmailRecipientFields
            toAddress={toAddress}
            ccAddress={ccAddress}
            bccAddress={bccAddress}
            subject={subject}
            showCcBcc={showCcBcc}
            setToAddress={setToAddress}
            setCcAddress={setCcAddress}
            setBccAddress={setBccAddress}
            setSubject={setSubject}
            setShowCcBcc={setShowCcBcc}
            handleChange={handleChange}
          />
        </div>

        {/* Email Body */}
        <div className="flex-1 px-3 pb-2 min-h-0">
          <RichEmailEditor
            ref={editorRef}
            value={bodyHtml}
            onChange={(html) => {
              setBodyHtml(html);
              handleChange();
            }}
            minHeight={isMaximized ? 'calc(100vh - 320px)' : '220px'}
            maxHeight={isMaximized ? undefined : '16rem'}
            placeholder="Write your message…"
          />
        </div>
      </div>

      {/* Bottom Toolbar */}
      <EmailBottomToolbar
        onSend={handleSend}
        onSaveDraft={handleSaveDraft}
        onClose={handleCloseWithAutoSave}
        isMaximized={isMaximized}
        isSending={isSending}
        isSavingDraft={isSavingDraft}
        leadId={leadId}
        contactId={contactId}
        subject={subject}
        bodyHtml={bodyHtml}
        onSubjectChange={setSubject}
        onBodyHtmlChange={setBodyFromTemplate}
        onChange={handleChange}
        onSignatureSelect={(sig) => {
          currentSignature.current = sig;
          applySignature(sig);
        }}
        appliedSignatureId={appliedSignatureId}
        onRecipientResolved={(email) => {
          if (!toAddress.trim()) setToAddress(email);
        }}
      />
    </div>
  );
};

export default ComposeEmail;
